import { useEffect, useRef, useState, type ReactNode } from "react"
import { useLocation } from "react-router-dom"
import { useAppBackground } from "../lib/useAppBackground"
import { APP_BG_CLASSES } from "../lib/appBackground"
import { SidebarV2 } from "./SidebarV2"
import { GlitchToggle } from "./GlitchToggle"
import { PortalStrip } from "./PortalStrip"
import { TrialBanner } from "./TrialBanner"
import { OnboardingSpotlight } from "./OnboardingSpotlight"
import { GlobalReportModals } from "./GlobalReportModals"
import { CortexModalProvider } from "./CortexModalContext"
import { CortexFAB } from "./CortexFAB"
import { isEmbedded } from "../lib/isEmbedded"
import { ROUTES } from "../lib/routes"
import { useBranding } from "../lib/useBranding"
import { setBrandingGlitchOverride } from "../lib/glitchEnabled"
import { setTerminalTraceOverride } from "../lib/useTerminalTraceEnabled"

interface LayoutV2Props {
  children: ReactNode
}

const SIDEBAR_MS = 200
/** Delay before menu/hamburger button fades in after sidebar closes. */
const MENU_BUTTON_FADE_DELAY_MS = 150

/**
 * V2 Layout: Sidebar starts closed; overlay behavior (no content shift) from desktop down.
 * Hamburger + PortalStrip share a fixed top-bar header; header height drives content padding-top
 * via --top-bar-h CSS var (ResizeObserver). Embedded: collapsed overlay, no top bar.
 */
export function LayoutV2({ children }: LayoutV2Props) {
  const location = useLocation()
  const [appBg] = useAppBackground()
  const { branding } = useBranding()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [hamburgerVisible, setHamburgerVisible] = useState(true)
  const [notifUnreadCount, setNotifUnreadCount] = useState(0)
  const embedded = isEmbedded()
  const showOverlayUi = !embedded
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const headerRef = useRef<HTMLElement>(null)
  /** Hide FAB on dashboard (Cortex feed is there); keep on Topica and other routes for reference. */
  const showCortexFAB = showOverlayUi && location.pathname !== ROUTES.dashboard

  useEffect(() => {
    setBrandingGlitchOverride(branding?.glitchEffectsEnabled ?? true)
    setTerminalTraceOverride(branding?.terminalTraceEnabled ?? true)
  }, [branding?.glitchEffectsEnabled, branding?.terminalTraceEnabled])

  // Keep --top-bar-h CSS var in sync with the actual header height so main
  // padding-top stays accurate regardless of strip presence or text wrapping.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () => {
      document.documentElement.style.setProperty("--top-bar-h", `${el.offsetHeight}px`)
    }
    update()
    const obs = new ResizeObserver(update)
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (embedded) document.documentElement.classList.add("embedded")
    const t = window.setTimeout(() => {
      if (typeof window !== "undefined") window.focus()
    }, 0)
    return () => {
      window.clearTimeout(t)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [embedded])

  const openSidebar = () => {
    setSidebarOpen(true)
    setHamburgerVisible(false)
  }

  const closeSidebar = () => {
    setSidebarOpen(false)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setHamburgerVisible(true)
      timeoutRef.current = null
    }, MENU_BUTTON_FADE_DELAY_MS)
  }

  return (
    <CortexModalProvider>
      <div className="min-h-screen overflow-x-clip text-white font-sans selection:bg-white/10">
        <SidebarV2
          isMobileOpen={sidebarOpen}
          onMobileClose={closeSidebar}
          forceCollapsed={embedded}
          overlayDefault
          isDocked={false}
          onUnreadCountChange={setNotifUnreadCount}
        />

        {/* Fixed top bar: hamburger + portal strip share the same reserved area.
            pointer-events-none on the container lets clicks pass through transparent gaps. */}
        {showOverlayUi && (
          <header
            ref={headerRef}
            className="fixed inset-x-0 top-0 z-[60] pointer-events-none px-4 md:px-6"
            style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))", paddingBottom: "0.625rem" }}
          >
            <div className="max-w-6xl mx-auto flex items-center gap-3">
              {/* Hamburger */}
              <button
                type="button"
                onClick={openSidebar}
                className={`relative pointer-events-auto shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-black/60 backdrop-blur-xl hover:bg-black/80 border border-white/[0.10] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/30 transition-opacity duration-150
                  ${hamburgerVisible && !sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
                `}
                aria-label="Open menu"
              >
                <GlitchToggle isOpen={false} className="text-xl" />
                {notifUnreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-[#141210] pointer-events-none" />
                )}
              </button>

              {/* Strip fills remaining width; renders nothing when hidden (role, route, dismiss, config) */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <PortalStrip />
                <TrialBanner />
              </div>
            </div>
          </header>
        )}

        <div className={`min-h-screen flex flex-col ${APP_BG_CLASSES[appBg]}`}>
          <main
            className="flex-1 p-4 md:p-6"
            style={{ paddingTop: "var(--top-bar-h, 5rem)" }}
          >
            {children}
          </main>
        </div>
        {/* FAB hidden for now — Cortex still accessible via sidebar and other entry points */}
        {/* {showCortexFAB && <CortexFAB />} */}
        <GlobalReportModals />
        <OnboardingSpotlight />
      </div>
    </CortexModalProvider>
  )
}
